import React from "react";
import "./OutletPage.css";
import { CheckCircle, ArrowRight, Play } from "lucide-react";

import heroImg       from "../image/gen-cloud-hero.png";
import centralImg    from "../image/gen-cloud-central.png";
import virtualImg    from "../image/gen-cloud-brands.png";
import multiOrderImg from "../image/gen-cloud-orders.png";
import kdsImg        from "../image/gen-cloud-kds.png";
import reconcileImg  from "../image/gen-cloud-reconcile.png";
import reportImg     from "../image/gen-cloud-reports.png";
import demoImg       from "../image/DemoForm_img.png";

const GRADIENT = "linear-gradient(135deg, #06b6d4, #0891b2)";
const GLOW     = "rgba(6,182,212,0.3)";

const css = `
  .op-cloud { --op-accent:#06b6d4; --op-gradient:${GRADIENT}; --op-glow:${GLOW};
    --op-accent-10:rgba(6,182,212,0.1); --op-accent-30:rgba(6,182,212,0.3);
    --op-accent-05:rgba(6,182,212,0.05); }
`;

export default function CloudKitchenOutletPage() {
  return (
    <div className="op-page op-cloud">
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="op-hero">
        <div className="op-hero__bg">
          <img src={heroImg} alt="Cloud Kitchen" />
          <div className="op-hero__gradient" />
          <div className="op-hero__noise" />
        </div>
        <div className="op-hero__content">
          <div className="op-hero__eyebrow">
            <span className="op-hero__eyebrow-dot" />
            Cloud Kitchen POS
          </div>
          <h1 className="op-hero__title">
            Multiple brands,<br />
            <span className="op-accent-text">one kitchen</span>
          </h1>
          <p className="op-hero__subtitle">
            Run 5 virtual brands from one kitchen. Manage Swiggy, Zomato, and your own website orders on a single screen. KadaiPOS keeps every order on track.
          </p>
          <div className="op-hero__actions">
            <button
              className="op-btn-primary"
              onClick={() => document.getElementById("op-demo")?.scrollIntoView({ behavior: "smooth" })}
            >
              Get Free Demo <ArrowRight size={18} />
            </button>
            <button className="op-btn-ghost"><Play size={16} /> Watch Demo</button>
          </div>
          <div className="op-hero__stats">
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">5+</span>
              <span className="op-hero__stat-label">Brands per Kitchen</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">All</span>
              <span className="op-hero__stat-label">Aggregators</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Unified</span>
              <span className="op-hero__stat-label">Dashboard</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Live</span>
              <span className="op-hero__stat-label">Order Status</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENTO ── */}
      <section className="op-bento">
        <div className="op-bento__header">
          <div className="op-section-eyebrow">Platform Features</div>
          <h2 className="op-section-title">Your cloud kitchen control room</h2>
          <p className="op-section-subtitle">
            Scale from one brand to ten without adding staff or complexity. KadaiPOS does the heavy lifting.
          </p>
        </div>

        <div className="op-bento__grid">
          {/* Accent narrow – Virtual Brands */}
          <div className="op-card op-card--narrow op-card--accent">
            <div className="op-card__body" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>🏷️</div>
              <h3 className="op-card__title" style={{ color: "#fff" }}>Virtual Brand Manager</h3>
              <p className="op-card__desc" style={{ color: "rgba(255,255,255,0.75)" }}>
                Launch and manage multiple delivery-only brands from a single kitchen — each with its own menu, pricing, and reports.
              </p>
              <ul className="op-card__list">
                <li>Separate menu per virtual brand</li>
                <li>Brand-wise sales reports</li>
                <li>Shared kitchen inventory</li>
              </ul>
            </div>
          </div>

          {/* Wide – Multi Orders */}
          <div className="op-card op-card--wide">
            <img src={multiOrderImg} alt="Multiple Orders" className="op-card__img" />
            <div className="op-card__body">
              <div className="op-card__tag">📲 Order Aggregation</div>
              <h3 className="op-card__title">All orders in one queue — Swiggy, Zomato, website</h3>
              <p className="op-card__desc">
                No more switching between tablets. Every online order from every platform lands in one unified order queue on your POS screen.
              </p>
              <ul className="op-card__list">
                <li>Auto-accept online orders from all aggregators</li>
                <li>Brand-wise and platform-wise order separation</li>
                <li>Estimated prep time pushed back to delivery app</li>
                <li>Out-of-stock sync across all platforms simultaneously</li>
              </ul>
            </div>
          </div>

          {/* Half – Virtual Brand image */}
          <div className="op-card op-card--half">
            <img src={virtualImg} alt="Virtual Brand" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">🌐 Virtual Brands</div>
              <h3 className="op-card__title">One kitchen → many brands → more revenue</h3>
              <p className="op-card__desc">Launch a new cuisine brand in minutes. Same kitchen resources, brand-new revenue stream on Swiggy / Zomato.</p>
            </div>
          </div>

          {/* Half – KDS */}
          <div className="op-card op-card--half">
            <img src={kdsImg} alt="Kitchen Display" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">📺 Kitchen Display System</div>
              <h3 className="op-card__title">Your chef sees orders the moment they come in</h3>
              <p className="op-card__desc">KDS screen shows order items, brand name, and platform. Chef marks each item ready — triggers delivery partner notification.</p>
            </div>
          </div>

          {/* Full – Central Kitchen */}
          <div className="op-card op-card--full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div className="op-card__body" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div className="op-card__tag">🏭 Central Kitchen</div>
              <h3 className="op-card__title">Centrally produced, outlet-specific pricing</h3>
              <p className="op-card__desc">
                Produce in your central kitchen, transfer to dark stores or satellite kitchens. Each outlet type (own, franchise, third-party) gets its own transfer and retail pricing.
              </p>
              <ul className="op-card__list">
                <li>Production batch tracking by recipe</li>
                <li>Warehouse → branch stock transfer with pricing</li>
                <li>Outlet-type pricing: own outlet vs franchise vs third party</li>
                <li>Transfer price vs retail price margin tracking</li>
              </ul>
            </div>
            <img src={centralImg} alt="Central Kitchen" className="op-card__img" style={{ height: "100%", minHeight: 260 }} />
          </div>
        </div>
      </section>

      {/* ── DEEP DIVE ── */}
      <section className="op-deepdive">
        <div className="op-deepdive__inner">
          <div className="op-bento__header">
            <div className="op-section-eyebrow">Reconciliation &amp; Reports</div>
            <h2 className="op-section-title">Know exactly what you earned — and from where</h2>
          </div>

          <div className="op-deepdive__grid">
            <div className="op-deepdive__img-wrap">
              <img src={reconcileImg} alt="Order Reconciliation" />
              <div className="op-deepdive__img-badge">💰 Reconciliation</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Online order reconciliation — no surprises from aggregators</h3>
              <p>
                Match every Swiggy and Zomato payout against actual orders. Spot missed payouts, commission discrepancies, and wrong charges before they pile up.
              </p>
              <ul className="op-check-list">
                {[
                  "Platform-wise daily payout matching",
                  "Commission rate verification per order",
                  "Cancelled order refund tracking",
                  "Export reconciliation report for accountant",
                  "Alert when payout differs from expected"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="op-deepdive__grid op-deepdive__grid--reverse" style={{ marginTop: 60 }}>
            <div className="op-deepdive__img-wrap">
              <img src={reportImg} alt="Compact Reports" />
              <div className="op-deepdive__img-badge">📊 Reports</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Compact reporting — big insights, zero clutter</h3>
              <p>
                Owner dashboard shows today's orders, revenue, cancellations, and platform splits at a glance. Drill down to brand, item, or hour-wise data on demand.
              </p>
              <ul className="op-check-list">
                {[
                  "Brand-wise revenue &amp; order count",
                  "Platform-wise breakdown (Swiggy vs Zomato vs own website)",
                  "Item-wise bestseller ranking",
                  "Peak hour analysis to staff accordingly",
                  "Day-end closing with full audit trail"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── MINI STRIP ── */}
      <section className="op-strip">
        <div className="op-strip__grid">
          {[
            { icon: "⏱️", title: "Prep Time Control", desc: "Push accurate prep times to delivery apps — fewer cancellations, better ratings." },
            { icon: "🔕", title: "Menu On/Off", desc: "Take any item off all platforms in one tap when you run out during service." },
            { icon: "📦", title: "Packaging Charges", desc: "Auto-add packaging fees per order, per brand, per item — fully configurable." },
            { icon: "🔐", title: "Role Access", desc: "Owner, kitchen manager, delivery coordinator — separate logins with separate permissions." },
          ].map((f, i) => (
            <div className="op-mini-card" key={i}>
              <div className="op-mini-card__icon">{f.icon}</div>
              <div className="op-mini-card__title">{f.title}</div>
              <p className="op-mini-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="op-cta" id="op-demo">
        <div className="op-cta__inner">
          <div className="op-cta__left">
            <h2>Scale your cloud kitchen without the chaos</h2>
            <p>See how KadaiPOS consolidates all your orders, brands, and reports in one screen. Free demo — no commitment required.</p>
            <div className="op-cta__form">
              <input className="op-cta__input" placeholder="Your name *" />
              <div className="op-cta__row">
                <input className="op-cta__input" placeholder="Phone number *" />
                <input className="op-cta__input" placeholder="City *" />
              </div>
              <input className="op-cta__input" placeholder="Kitchen name *" />
              <button className="op-cta__submit">Book Free Demo →</button>
            </div>
          </div>
          <div className="op-cta__right-img">
            <img src={demoImg} alt="Demo" />
          </div>
        </div>
      </section>
    </div>
  );
}
